import { useState, useEffect } from "react";
import { apiGet, apiPost } from "../api/client";
import { PageHeader } from "../components/ui/Primitives";
import { fmtKst } from "../lib/format";
import { Bell, Send, CheckCircle2, ShieldAlert, RefreshCw } from "lucide-react";

export default function Alerts() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const data = await apiGet<any>("/alerts/recent?limit=50");
      setAlerts(data.alerts || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await apiGet<any>("/alerts/settings");
      setSettings(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchAlerts();
    fetchSettings();
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleAck = async (alertId: string) => {
    try {
      await apiPost(`/alerts/acknowledge/${alertId}`);
      setAlerts(alerts.filter((a) => a.alert_id !== alertId));
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Bell size={24} color="var(--accent)" />}
        title="알림 및 통보 센터"
        description="텔레그램 봇 및 웹 관제탑 실시간 비상 알림 현황 및 인시던트 수신 제어"
        actions={
          <button className="btn-secondary" onClick={fetchAlerts} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} /> 새로고침
          </button>
        }
      />

      {/* Alert Settings */}
      {settings && (
        <div className="grid grid-2" style={{ marginBottom: "var(--space-4)" }}>
          <div className="card">
            <div className="card-title-row" style={{ marginBottom: "var(--space-2)" }}>
              <Send size={16} color="var(--accent)" />
              <div className="stat-label">텔레그램 봇 연동 (Telegram)</div>
            </div>
            <div className="stat-value" style={{ fontSize: 18 }}>
              <span className={`badge ${settings.telegram_enabled ? "badge-green" : "badge-red"}`}>
                {settings.telegram_enabled ? "활성화됨 (ENABLED)" : "비활성화 (DISABLED)"}
              </span>
            </div>
            {settings.telegram_chat_id && (
              <div className="muted-note">
                수신 채팅 ID: <strong style={{ color: "var(--text-primary)" }}>{settings.telegram_chat_id}</strong>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title-row" style={{ marginBottom: "var(--space-2)" }}>
              <Bell size={16} color="var(--purple)" />
              <div className="stat-label">관제탑 인앱 알림 (In-App)</div>
            </div>
            <div className="stat-value" style={{ fontSize: 18 }}>
              <span className={`badge ${settings.in_app_enabled ? "badge-green" : "badge-red"}`}>
                {settings.in_app_enabled ? "활성화됨 (ENABLED)" : "비활성화 (DISABLED)"}
              </span>
            </div>
            <div className="muted-note">
              실시간 웹소켓 이벤트 팝업 및 헤더 경보 연동
            </div>
          </div>
        </div>
      )}

      {/* Alert List */}
      <div className="card">
        <div className="card-title-row">
          <ShieldAlert size={18} color="var(--orange)" />
          <h3 className="card-title">최근 발생 알림 ({alerts.length}건)</h3>
          <span className="card-title-meta">미확인 이벤트 실시간 큐</span>
        </div>
        {alerts.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "32px 20px", textAlign: "center", fontStyle: "italic" }}>
            현재 미확인된 위험 알림이 없습니다 (시스템 정상 상태)
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>발생 시각</th>
                <th>위험 등급</th>
                <th>발생 출처</th>
                <th>알림 메시지</th>
                <th>확인 처리</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a, i) => (
                <tr key={i}>
                  <td style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)", whiteSpace: "nowrap" }}>
                    {a.ts ? fmtKst(new Date(a.ts * 1000)) : "—"}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        a.severity === "critical"
                          ? "badge-red"
                          : a.severity === "warning"
                          ? "badge-yellow"
                          : "badge-blue"
                      }`}
                    >
                      {a.severity === "critical"
                        ? "심각 (Critical)"
                        : a.severity === "warning"
                        ? "경고 (Warning)"
                        : "정보 (Info)"}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>{a.alert_key || a.source || "—"}</td>
                  <td style={{ fontSize: 13, color: "var(--text-primary)" }}>{a.message}</td>
                  <td>
                    {a.alert_id && (
                      <button
                        className="btn-primary"
                        style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }}
                        onClick={() => handleAck(a.alert_id)}
                      >
                        <CheckCircle2 size={12} /> 확인 (Ack)
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
